import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import api from '../services/api';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';

const BusinessReturns = ({ projectMode = false }) => {
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  const routeBase = useWorkspaceBasePath();

  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [goToPage, setGoToPage] = useState('');
  const itemsPerPage = 20;

  useEffect(() => { loadReturns(); }, []);

  // Reset page on filter/search change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterType, filterPeriod]);

  const loadReturns = async () => {
    try {
      const res = await api.get(`${apiBase}/returns`);
      setReturns(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = returns.filter(r => {
    // Text search
    const matchSearch = !searchQuery ||
      r.numero_avoir?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.client_nom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.numero_facture?.toLowerCase().includes(searchQuery.toLowerCase());

    // Type filter
    const matchType = filterType === 'all' || r.type_remboursement === filterType;

    // Period filter
    let matchPeriod = true;
    if (filterPeriod !== 'all') {
      const date = new Date(r.date_retour);
      const now = new Date();
      if (filterPeriod === 'today') {
        matchPeriod = date.toDateString() === now.toDateString();
      } else if (filterPeriod === 'week') {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        matchPeriod = date >= weekAgo;
      } else if (filterPeriod === 'month') {
        matchPeriod = date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      } else if (filterPeriod === 'year') {
        matchPeriod = date.getFullYear() === now.getFullYear();
      }
    }

    return matchSearch && matchType && matchPeriod;
  });

  // Pagination
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

  // Stats
  const totalCredits = returns.filter(r => r.type_remboursement === 'credit_client').length;
  const totalRemboursements = returns.filter(r => r.type_remboursement === 'remboursement_direct').length;
  const totalMontant = filtered.reduce((s, r) => s + parseFloat(r.total || 0), 0);

  const handleGoToPage = (e) => {
    e.preventDefault();
    const page = parseInt(goToPage);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setGoToPage('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 lg:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Avoirs / Retours</h1>
          <p className="text-gray-500">{returns.length} avoir(s) enregistré(s)</p>
        </div>
      </div>

      {/* Stats */}
      {returns.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl border p-3 text-center">
            <div className="text-2xl font-bold text-gray-800">{returns.length}</div>
            <div className="text-xs text-gray-500">Total avoirs</div>
          </div>
          <div className="bg-white rounded-xl border p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{totalCredits}</div>
            <div className="text-xs text-gray-500">Crédits client</div>
          </div>
          <div className="bg-white rounded-xl border p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{totalRemboursements}</div>
            <div className="text-xs text-gray-500">Remboursements</div>
          </div>
          <div className="bg-white rounded-xl border p-3 text-center">
            <div className="text-2xl font-bold text-red-600">{totalMontant.toLocaleString()}</div>
            <div className="text-xs text-gray-500">Montant filtré (XOF)</div>
          </div>
        </div>
      )}

      {/* Filters & Search */}
      {returns.length > 0 && (
        <div className="bg-white rounded-xl border p-4 mb-6 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="🔍 Rechercher par numéro, client, facture..."
              className="flex-1 border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500 text-sm"
            />
            {/* Type filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border rounded-xl px-3 py-2 focus:ring-2 focus:ring-indigo-500 text-sm bg-white"
            >
              <option value="all">Tous les types</option>
              <option value="credit_client">Crédit client</option>
              <option value="remboursement_direct">Remboursement direct</option>
            </select>
            {/* Period filter */}
            <select
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
              className="border rounded-xl px-3 py-2 focus:ring-2 focus:ring-indigo-500 text-sm bg-white"
            >
              <option value="all">Toutes les périodes</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">7 derniers jours</option>
              <option value="month">Ce mois</option>
              <option value="year">Cette année</option>
            </select>
          </div>
          {/* Active filters info */}
          {(filterType !== 'all' || filterPeriod !== 'all' || searchQuery) && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">
                {filtered.length} résultat(s) sur {returns.length}
              </span>
              <button
                onClick={() => { setSearchQuery(''); setFilterType('all'); setFilterPeriod('all'); }}
                className="text-indigo-600 hover:text-indigo-800 font-medium text-xs"
              >
                ✕ Réinitialiser les filtres
              </button>
            </div>
          )}
        </div>
      )}

      {/* List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <span className="text-6xl mb-4 block">📋</span>
          <h3 className="text-xl font-bold text-gray-700 mb-2">
            {searchQuery || filterType !== 'all' || filterPeriod !== 'all' ? 'Aucun résultat' : 'Aucun avoir'}
          </h3>
          <p className="text-gray-500">
            {searchQuery || filterType !== 'all' || filterPeriod !== 'all'
              ? 'Essayez avec d\'autres filtres'
              : 'Les avoirs apparaîtront ici après un retour de produit sur une facture.'}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {paginated.map(r => (
              <Link
                key={r.id}
                to={`${routeBase}/returns/${r.id}`}
                className="block bg-white rounded-2xl shadow-sm border hover:shadow-md transition p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                      <span className="text-xl">🔄</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-800">{r.numero_avoir}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.type_remboursement === 'credit_client' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {r.type_remboursement === 'credit_client' ? 'Crédit' : 'Remboursement'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {r.client_nom || 'Sans client'} • Facture {r.numero_facture}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {new Date(r.date_retour).toLocaleDateString('fr-FR')}
                        {r.motif && ` • ${r.motif}`}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-red-600">{parseFloat(r.total).toLocaleString()} XOF</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 bg-white rounded-xl border px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-sm text-gray-500">
                {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filtered.length)} sur {filtered.length}
              </div>
              <div className="flex items-center gap-1 flex-wrap justify-center">
                {/* First */}
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                >
                  ««
                </button>
                {/* Prev */}
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                >
                  «
                </button>
                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  const showPages = new Set();
                  showPages.add(1);
                  showPages.add(totalPages);
                  for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
                    showPages.add(i);
                  }
                  const sortedPages = [...showPages].sort((a, b) => a - b);
                  sortedPages.forEach((page, idx) => {
                    if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                      pages.push(
                        <span key={`e-${page}`} className="px-1 sm:px-2 text-xs sm:text-sm text-gray-400">...</span>
                      );
                    }
                    pages.push(
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-2 sm:px-3 py-1 rounded text-xs sm:text-sm ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                      >
                        {page}
                      </button>
                    );
                  });
                  return pages;
                })()}
                {/* Next */}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                >
                  »
                </button>
                {/* Last */}
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                >
                  »»
                </button>
              </div>
              {/* Go to page - for large datasets */}
              {totalPages > 10 && (
                <form onSubmit={handleGoToPage} className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">Page</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={goToPage}
                    onChange={(e) => setGoToPage(e.target.value)}
                    placeholder={`${currentPage}`}
                    className="w-16 border rounded px-2 py-1 text-xs text-center focus:ring-1 focus:ring-indigo-500"
                  />
                  <span className="text-xs text-gray-500">/ {totalPages}</span>
                  <button type="submit" className="px-2 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700">
                    OK
                  </button>
                </form>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BusinessReturns;
